import { Check, ChevronLeft, ChevronRight, History } from 'lucide-react';

const PHASES = [
  {
    id: 'clear',
    label: 'Get Clear',
    color: 'bg-accent',
    steps: [
      { id: 1, label: 'Process Inbox' },
      { id: 2, label: 'Mind Sweep' }
    ]
  },
  {
    id: 'current',
    label: 'Get Current',
    color: 'bg-weekly',
    steps: [
      { id: 3, label: 'Week in Review' },
      { id: 4, label: 'Review Projects' },
      { id: 5, label: 'Waiting For' },
      { id: 6, label: 'Calendar' }
    ]
  },
  {
    id: 'creative',
    label: 'Get Creative',
    color: 'bg-monthly',
    steps: [
      { id: 7, label: 'Someday & Goals' }
    ]
  }
];

const ALL_STEPS = PHASES.flatMap(p => p.steps);
const TOTAL_STEPS = ALL_STEPS.length;

function getCurrentPhase(stepId) {
  for (const phase of PHASES) {
    if (phase.steps.some(s => s.id === stepId)) {
      return phase;
    }
  }
  return PHASES[0];
}

export default function WeeklyReviewWizard({
  currentStep,
  onStepChange,
  onFinish,
  onShowHistory,
  canAdvance = true,
  inboxCount = 0,
  processedCount = 0
}) {
  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      onStepChange(currentStep + 1);
    } else {
      onFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      onStepChange(currentStep - 1);
    }
  };

  const currentPhase = getCurrentPhase(currentStep);
  const currentStepData = ALL_STEPS.find(s => s.id === currentStep);

  return (
    <div className="bg-surface-card border-b border-border">
      {/* Phase indicator */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border/50">
        {PHASES.map((phase, idx) => {
          const phaseSteps = phase.steps;
          const firstStep = phaseSteps[0].id;
          const lastStep = phaseSteps[phaseSteps.length - 1].id;
          const isActive = currentStep >= firstStep && currentStep <= lastStep;
          const isCompleted = currentStep > lastStep;

          return (
            <div key={phase.id} className="flex items-center">
              <button
                onClick={() => firstStep <= currentStep && onStepChange(firstStep)}
                disabled={firstStep > currentStep}
                className={`
                  px-2.5 py-1 rounded-md text-xs font-medium transition-all
                  border-none cursor-pointer
                  ${isActive
                    ? `${phase.color} text-white`
                    : isCompleted
                      ? 'bg-success/20 text-success'
                      : 'bg-surface-hover text-text-tertiary cursor-not-allowed'
                  }
                `}
              >
                {isCompleted && <Check size={10} className="inline mr-1" />}
                {phase.label}
              </button>
              {idx < PHASES.length - 1 && (
                <div className={`w-6 h-0.5 mx-1 rounded-full ${
                  isCompleted ? 'bg-success' : 'bg-border'
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Step navigation */}
      <div className="flex items-center gap-3 px-4 py-2.5">
        {/* Back button */}
        <button
          onClick={handleBack}
          disabled={currentStep === 1}
          className={`
            p-1.5 rounded-lg transition-all border-none cursor-pointer
            ${currentStep === 1
              ? 'text-text-muted cursor-not-allowed bg-transparent'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
            }
          `}
        >
          <ChevronLeft size={18} />
        </button>

        {/* Step dots for current phase */}
        <div className="flex items-center gap-1.5">
          {currentPhase.steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => step.id <= currentStep && onStepChange(step.id)}
                disabled={step.id > currentStep}
                className={`
                  w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold
                  transition-all duration-200 border-none cursor-pointer
                  ${step.id < currentStep
                    ? 'bg-success text-white'
                    : step.id === currentStep
                      ? `${currentPhase.color} text-white`
                      : 'bg-surface-hover text-text-muted cursor-not-allowed'
                  }
                `}
              >
                {step.id < currentStep ? (
                  <Check size={12} strokeWidth={3} />
                ) : (
                  step.id
                )}
              </button>
              {index < currentPhase.steps.length - 1 && (
                <div className={`w-4 h-0.5 mx-0.5 rounded-full ${
                  step.id < currentStep ? 'bg-success' : 'bg-border'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Current step label */}
        <span className="text-sm font-medium text-text-primary">
          {currentStepData?.label}
        </span>

        {/* Progress indicator for inbox step */}
        {currentStep === 1 && inboxCount > 0 && (
          <span className="text-xs text-text-tertiary bg-surface-hover px-2 py-0.5 rounded-full">
            {processedCount}/{processedCount + inboxCount}
          </span>
        )}

        {/* Step counter */}
        <span className="text-xs text-text-tertiary">
          Step {currentStep} of {TOTAL_STEPS}
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* History button */}
        {onShowHistory && (
          <button
            onClick={onShowHistory}
            className="flex items-center gap-1 text-xs text-text-tertiary hover:text-text-secondary transition-colors border-none bg-transparent cursor-pointer"
            title="View review history"
          >
            <History size={14} />
            <span className="hidden md:inline">History</span>
          </button>
        )}

        {/* Skip button (optional) */}
        {currentStep > 1 && currentStep < TOTAL_STEPS && (
          <button
            onClick={() => onStepChange(currentStep + 1)}
            className="text-xs text-text-tertiary hover:text-text-secondary transition-colors border-none bg-transparent cursor-pointer"
          >
            Skip
          </button>
        )}

        {/* Next/Finish button */}
        <button
          onClick={handleNext}
          disabled={!canAdvance}
          className={`
            flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium
            transition-all duration-200 border-none cursor-pointer
            ${canAdvance
              ? `${currentPhase.color} text-white hover:opacity-90`
              : 'bg-surface-hover text-text-muted cursor-not-allowed'
            }
          `}
        >
          {currentStep === TOTAL_STEPS ? 'Complete Review' : 'Next'}
          {currentStep < TOTAL_STEPS && <ChevronRight size={16} />}
        </button>
      </div>
    </div>
  );
}
